import { useEffect, useState } from "react";
import { Save, Sparkles, Loader2, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import MicButton from "@/components/ui/MicButton";
import PrevisionnelSelect from "@/components/memo/PrevisionnelSelect";
import TresorerieChart from "@/components/memo/TresorerieChart";
import SafeBlock from "@/components/ui/SafeBlock";
import ReportShell from "@/components/reports/ReportShell";
import { fetchMemoParams, saveMemoParams, startMemoJob, fetchMemoJob, fetchLastMemo, downloadMemoPdf } from "@/lib/memo";

const BRIEF = [
    ["contexte", "Contexte de l'analyse"], ["inclure", "Éléments à inclure impérativement"],
    ["exclure", "Éléments à exclure / masquer"], ["hypotheses", "Hypothèses de restructuration (ex : licenciements, renégociations)"],
];

export default function MemoFlashSection({ dossierId }) {
    const [brief, setBrief] = useState({});
    const [prevId, setPrevId] = useState("");
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [stage, setStage] = useState("");
    const [memo, setMemo] = useState("");
    const [memoDate, setMemoDate] = useState(null);
    const [chart, setChart] = useState(null);
    const [charts, setCharts] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        fetchMemoParams(dossierId).then((p) => setBrief(p.parametres_rapport || {})).catch(() => {});
        fetchLastMemo(dossierId).then((m) => { if (m) { setMemo(m.content); setMemoDate(m.created_at); setChart(m.chart || null); setCharts(m.charts || []); setSessionId(m.session_id); } }).catch(() => {});
    }, [dossierId]);

    async function exportPdf() {
        setExporting(true);
        try { await downloadMemoPdf(dossierId); toast.success("PDF Mémo Flash téléchargé"); }
        catch (e) { toast.error("Export PDF impossible", { description: e.message }); }
        finally { setExporting(false); }
    }

    async function save() {
        setSaving(true);
        try {
            const briefing = { contexte: brief.contexte || "", inclure: brief.inclure || "", exclure: brief.exclure || "", hypotheses: brief.hypotheses || "" };
            await saveMemoParams(dossierId, { parametres_rapport: briefing });
            toast.success("Briefing enregistré");
            return true;
        } catch (e) { toast.error("Enregistrement impossible", { description: e.message }); return false; }
        finally { setSaving(false); }
    }

    async function generate() {
        if (generating) return;
        if (!(await save())) return;
        setGenerating(true); setMemo(""); setChart(null); setSessionId(null); setStage("preparation");
        const startedAt = Date.now();
        try {
            const { job_id } = await startMemoJob(dossierId, { previsionnel_id: prevId || null });
            let lost = 0;
            for (;;) {
                await new Promise((r) => setTimeout(r, 3000));
                let j;
                try { j = await fetchMemoJob(dossierId, job_id); }
                catch (e) {
                    if (/introuvable|indisponible|Erreur 5\d\d/i.test(e.message) && lost++ < 4) {
                        const m = await fetchLastMemo(dossierId).catch(() => null);
                        if (m && new Date(m.created_at).getTime() >= startedAt - 60000) j = { stage: "done", content: m.content, chart: m.chart, session_id: m.session_id };
                        else continue;
                    } else throw e;
                }
                setMemo(j.content || ""); setStage(j.stage);
                if (j.stage === "done") { setChart(j.chart || null); setSessionId(j.session_id); setCharts([]); setMemoDate(new Date().toISOString()); toast.success("Mémo Flash généré"); break; }
                if (j.stage === "error") { toast.error("Erreur IA", { description: String(j.error), duration: 12000 }); break; }
            }
        } catch (e) { toast.error("Génération impossible", { description: e.message }); }
        finally { setGenerating(false); setStage(""); }
    }

    const locked = saving || generating;
    return (
        <section data-testid="memo-flash-section" className="space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h2 className="font-display text-2xl text-foreground">Préparation Mémo Flash</h2>
                    <p className="text-xs text-muted-foreground">Briefing + grille prévisionnelle (saisie dans l&apos;onglet <span className="font-medium text-slate-700">Prévisionnels</span>) → l&apos;IA construit les scénarios et l&apos;argumentaire d&apos;urgence.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" data-testid="memo-save" onClick={save} disabled={locked} className="rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer</Button>
                    <Button type="button" data-testid="memo-generate" onClick={generate} disabled={locked} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">{generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {generating ? (stage === "generation" ? "Rédaction du mémo…" : stage === "graphique" ? "Construction du graphique…" : "Lecture du dossier…") : "Générer le Mémo Flash"}</Button>
                </div>
            </div>

            <Card className="border-slate-200 bg-white shadow-sm"><CardContent className="pt-6 space-y-4">
                <h3 className="font-display text-lg text-foreground">Briefing rapport</h3>
                <PrevisionnelSelect dossierId={dossierId} value={prevId} onChange={setPrevId} disabled={locked} testId="memo-previsionnel-ref" />
                {BRIEF.map(([k, label]) => (
                    <div key={k} className="space-y-1.5">
                        <label className="text-sm text-muted-foreground" htmlFor={`brief-${k}`}>{label}</label>
                        <div className="flex items-start gap-2">
                            <Textarea id={`brief-${k}`} data-testid={`brief-${k}`} rows={5} value={brief[k] || ""} onChange={(e) => setBrief((b) => ({ ...b, [k]: e.target.value }))} disabled={locked} className="bg-background resize-y text-sm min-h-[120px]" />
                            <MicButton testId={`brief-mic-${k}`} disabled={locked} onText={(t) => setBrief((b) => ({ ...b, [k]: (b[k] ? b[k] + " " : "") + t }))} />
                        </div>
                    </div>
                ))}
                <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
                    <Button type="button" variant="outline" data-testid="memo-save-bottom" onClick={save} disabled={locked} className="rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer</Button>
                    <Button type="button" data-testid="memo-generate-bottom" onClick={generate} disabled={locked} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"><Sparkles className="h-4 w-4" /> Générer le Mémo Flash</Button>
                </div>
            </CardContent></Card>

            {(memo || generating) && (
                <Card data-testid="memo-result" className="border-slate-200 bg-white shadow-sm"><CardContent className="pt-6 space-y-3">
                    <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /><h3 className="font-display text-lg text-foreground">Mémo Flash</h3>{memoDate && <span className="ml-auto text-xs text-muted-foreground">{new Date(memoDate).toLocaleString("fr-FR")}</span>}
                        {memo && !generating && <Button type="button" variant="outline" size="sm" data-testid="memo-export-pdf" onClick={exportPdf} disabled={exporting} className="rounded-full">{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export PDF</Button>}
                    </div>
                    <SafeBlock label="Le graphique de trésorerie">{chart && !generating && <TresorerieChart chart={chart} />}</SafeBlock>
                    <SafeBlock label="Le rapport"><ReportShell dossierId={dossierId} sessionId={sessionId} kind="memo_flash" content={memo} onContentChange={setMemo} charts={charts} onChartsChange={setCharts} streaming={generating} /></SafeBlock>
                    {!memo && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Génération en cours…</p>}
                </CardContent></Card>
            )}
        </section>
    );
}
